import { useEffect, useState } from "react";
import { TrendingUp, Flame } from "lucide-react";
import api from "../lib/api";
import DealCard from "./DealCard";

const TABS = [
  { key: "", label: "All" },
  { key: "travel", label: "Travel" },
  { key: "home", label: "Home & Garden" },
  { key: "electronics", label: "Electronics" },
  { key: "books", label: "Books & Media" },
  { key: "clothing", label: "Clothing" },
  { key: "food", label: "Food" },
  { key: "beauty", label: "Beauty" },
];

const TAB_STORAGE_KEY = "dealhub.trending.tab";

export default function TrendingNow() {
  const [deals, setDeals] = useState([]);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem(TAB_STORAGE_KEY) || ""; } catch (e) { return ""; }
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { limit: 6, hours: 168 }; // week window for the "This week in..." vibe
        if (tab) params.category = tab;
        const { data } = await api.get("/trending", { params });
        if (cancelled) return;
        setDeals(data?.deals || []);
        setSource(data?.source || "");
      } catch (err) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch (e) { /* ignore */ }
    return () => { cancelled = true; };
  }, [tab]);

  if (loading && deals.length === 0) return null;
  if (deals.length === 0) return null;

  const activeLabel = (TABS.find((t) => t.key === tab) || TABS[0]).label;
  const isActivity = source === "activity";

  return (
    <section className="py-16 border-t border-black/10" data-testid="trending-now-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-6 border-b border-black/10 pb-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2 inline-flex items-center gap-1.5">
            <Flame className="w-3 h-3" strokeWidth={2.5} />
            Trending now
          </div>
          <h2 className="font-serif font-black text-3xl md:text-4xl tracking-tighter">
            This week in <span className="text-[#E63946]">{activeLabel}</span>.
          </h2>
          <p className="mt-2 text-black/60 font-serif italic text-sm">
            {isActivity
              ? "Ranked by community bookmarks, clicks and reviews in the last 7 days."
              : "Freshly added picks — check back as shoppers weigh in."}
          </p>
        </div>

        {/* Category tab strip */}
        <div className="mb-8 flex items-center gap-1 overflow-x-auto pb-2 -mx-1 px-1" data-testid="trending-tabs">
          {TABS.map((t) => (
            <button
              key={t.key || "all"}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex-shrink-0 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] border-2 transition-colors ${
                tab === t.key
                  ? "bg-[#111111] text-white border-[#111111]"
                  : "bg-white text-black/60 border-black/10 hover:border-black hover:text-black"
              }`}
              data-testid={`trending-tab-${t.key || "all"}`}
            >
              {t.label}
            </button>
          ))}
          {isActivity && (
            <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-black/40 inline-flex items-center gap-1.5 flex-shrink-0 pl-3">
              <TrendingUp className="w-3 h-3" strokeWidth={2} />
              Updated hourly
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="trending-deals-grid">
          {deals.map((d, i) => (
            <div key={d.id} className="relative">
              {isActivity && i < 3 && (
                <div className="absolute -top-2 -left-2 z-20 bg-[#E63946] text-white font-serif font-black text-lg w-10 h-10 flex items-center justify-center" data-testid={`trending-rank-${i + 1}`}>
                  {i + 1}
                </div>
              )}
              <DealCard deal={d} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
